<template>
  <div class="range-container">
    <input
      type="range"
      class="range-slider"
      :class="[
        sizeClasses,
        {
          'range-disabled': disabled
        }
      ]"
      :min="min"
      :max="max"
      :step="step"
      :value="modelValue"
      :disabled="disabled"
      :style="sliderStyle"
      v-bind="$attrs"
      @input="handleInput"
      @change="handleChange"
    >
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: 0
  },
  min: {
    type: [Number, String],
    default: 0
  },
  max: {
    type: [Number, String],
    default: 100
  },
  step: {
    type: [Number, String],
    default: 1
  },
  size: {
    type: String,
    default: 'md',
    validator: val => ['sm', 'md', 'lg'].includes(val)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  color: {
    type: String,
    default: 'primary',
    validator: val => ['primary', 'accent'].includes(val)
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-1'
    case 'lg':
      return 'h-3'
    case 'md':
    default:
      return 'h-2'
  }
})

const progress = computed(() => {
  const value = Number(props.modelValue)
  const min = Number(props.min)
  const max = Number(props.max)
  return ((value - min) / (max - min)) * 100
})

const sliderStyle = computed(() => {
  const colorVar = props.color === 'accent' ? 'var(--color-accent)' : 'var(--forest-primary)'
  return {
    background: `linear-gradient(to right, 
      ${colorVar} 0%, 
      ${colorVar} ${progress.value}%, 
      var(--color-neutral-base) ${progress.value}%, 
      var(--color-neutral-base) 100%)`
  }
})

const handleInput = event => {
  emit('update:modelValue', Number(event.target.value))
}

const handleChange = event => {
  emit('change', Number(event.target.value))
}
</script>

<style scoped>
.range-slider {
  @apply w-full cursor-pointer appearance-none bg-transparent;
}

.range-slider::-webkit-slider-track {
  @apply w-full h-2 rounded-lg;
}

.range-slider::-webkit-slider-thumb {
  @apply appearance-none w-5 h-5 rounded-full bg-white border-2 border-rogue-deep-forest cursor-pointer shadow-lg;
  margin-top: -6px;
}

.range-slider::-moz-range-track {
  @apply w-full h-2 rounded-lg border-0;
}

.range-slider::-moz-range-thumb {
  @apply w-5 h-5 rounded-full bg-white border-2 border-rogue-deep-forest cursor-pointer shadow-lg;
  border: 2px solid var(--forest-primary);
}

.range-slider:hover::-webkit-slider-thumb {
  @apply shadow-xl transform scale-110;
  transition: all 0.2s ease;
}

.range-slider:hover::-moz-range-thumb {
  @apply shadow-xl transform scale-110;
  transition: all 0.2s ease;
}

.range-disabled {
  @apply opacity-50 cursor-not-allowed;
}

.range-disabled::-webkit-slider-thumb,
.range-disabled::-moz-range-thumb {
  @apply cursor-not-allowed;
}
</style>
